import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, CheckCircle, Clock, Phone, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/clients`);
        setClients(data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeCount = clients.filter(c => ['active', 'Active Client'].includes(c.status)).length;
  const inProgressCount = clients.filter(c => !['Completed', 'Inactive'].includes(c.status)).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a2e5a]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-dashboard">
        {/* Logo + Header */}
        <div className="flex flex-col items-center text-center pt-2">
          <img
            src="/supreme-logo.png"
            alt="Supreme Credit Inc."
            className="h-28 w-auto mb-3"
            data-testid="supreme-logo"
          />
          <h1 className="text-2xl font-semibold text-slate-900">Welcome back, {user?.name}</h1>
          <p className="text-slate-500 text-sm mt-1">Your referred clients overview</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white border-l-4 border-l-[#1a2e5a] shadow-sm" data-testid="stat-total">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Referred</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{clients.length}</p>
                </div>
                <Users className="w-8 h-8 text-[#1a2e5a] opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm" data-testid="stat-active">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Active</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{activeCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-l-4 border-l-amber-500 shadow-sm" data-testid="stat-progress">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">In Progress</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{inProgressCount}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Client List */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="clients-list">
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="text-lg font-semibold text-slate-900">Your Clients</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {clients.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No referred clients found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => navigate(`/partner/clients/${client.id}`)}
                    className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid={`partner-client-${client.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#1a2e5a]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-[#1a2e5a]">
                          {client.first_name?.[0]}{client.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{client.first_name} {client.last_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <Phone className="w-3 h-3" />
                          <span>{client.phone || 'No phone'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        client.status === 'Active Client' || client.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 text-xs'
                          : client.status === 'Collection Attention'
                          ? 'bg-amber-100 text-amber-800 text-xs'
                          : 'bg-slate-100 text-slate-800 text-xs'
                      }>
                        {client.status}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
